import React from 'react'
import FullScreenAsset from './FullScreenAsset'
import { useParams, useNavigate } from 'react-router'
import { useFetchWidenAsset } from '../../hooks/useFetchWidenAsset'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import EmptyLearningCenter from './EmptyLearningCenter'
import { useMountEffect } from '../../hooks/useMountEffect'
import useEndpoint from '../../hooks/useEndpoint'
import { extractedErrorObject, users } from '../../api/swagger'
import { useAuth } from '../Routes/AuthProvider'
import NoPermission from '../Elements/NoPermission'
import { useTranslation } from 'react-i18next'

const FullScreenContainer: React.FC = () => {
  const { t } = useTranslation()
  const { widenAssetKey, academicYear } = useParams<{
    widenAssetKey: string
    academicYear: string
  }>()
  const navigate = useNavigate()
  const { featureAbility } = useAuth()

  const { LearningCenter, BusinessContent } = useLoadingIds()
  const parsedAssetKey = parseInt(`${widenAssetKey}`)
  const { contentAsset, isLoading, fetchAsset, errorFetchingContent } =
    useFetchWidenAsset({
      assetKey: parsedAssetKey,
      loadingId: LearningCenter.fetchContentDetail,
    })

  const { filetype, embedUrl, isBusinessCenterContent } = contentAsset ?? {}

  const hasLearningCenterAccess = featureAbility.can(
    'learningCenter',
    'Feature'
  )
  const hasBusinessCenterAccess = featureAbility.can('business', 'Feature')

  const canAccessFullscreen = isBusinessCenterContent
    ? hasBusinessCenterAccess
    : hasLearningCenterAccess

  const { triggerFetch: checkNdaStatus } = useEndpoint({
    loadingId: BusinessContent.fetchUserNdaStatus,
    swaggerCall: async () => {
      try {
        const { hasSignedNda, assetRequiresNda } =
          await users.fetchUserNdaStatus({
            assetKey: parsedAssetKey,
          })

        if (!assetRequiresNda) {
          // Asset doesn't require NDA, continue to show content
          return
        }
        if (assetRequiresNda && hasSignedNda) {
          // NDA already signed, continue to show content
          return
        }

        navigate(
          {
            pathname: `/business/consent/${parsedAssetKey}`,
          },
          {
            replace: true,
            state: { fromFullscreen: true, academicYear },
          }
        )
      } catch (err) {
        const errorObj = await extractedErrorObject(err)
        navigate(
          {
            pathname: '/Account/profile',
          },
          {
            replace: true,
          }
        )
        throw new Error(errorObj?.message)
      }
    },
  })

  useMountEffect(() => {
    fetchAsset()
    checkNdaStatus()
  })

  if (errorFetchingContent || !contentAsset) {
    return <EmptyLearningCenter isLoading={isLoading} />
  }

  if (!canAccessFullscreen) {
    const message = t(
      'FullScreenContainer.NoPermission.message',
      `You don't have permission to view this content.`
    )
    return <NoPermission content={message} />
  }

  return <FullScreenAsset embedUrl={embedUrl} fileType={filetype} />
}

export default FullScreenContainer
